import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { GraduationCap, SignIn, Eye, EyeSlash } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const from = location.state?.from?.pathname || '/admin';

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');

    const result = await login(data.email, data.password);

    if (result.success) {
      navigate(from, { replace: true });
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen hero-split">
      {/* Image Side */}
      <div 
        className="hidden lg:block relative bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/159775/library-la-trobe-study-students-159775.jpeg)',
        }}
      >
        <div className="absolute inset-0 bg-[#E31E25]/40" />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center text-white">
            <GraduationCap size={80} weight="duotone" className="mx-auto mb-6" />
            <h1 className="text-4xl font-black tracking-tighter mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              EDSolutions 360
            </h1>
            <p className="text-white/80 text-lg max-w-md mx-auto">
              Sasurie College of Arts and Science (Autonomous) - Administrator Portal
            </p>
          </div>
        </div>
      </div>

      {/* Form Side */}
      <div className="flex flex-col justify-center p-6 sm:p-8 lg:p-16 xl:p-24 bg-white min-h-screen lg:min-h-0">
        <div className="max-w-md mx-auto lg:mx-0 w-full">
          {/* Mobile Header */}
          <div className="lg:hidden flex items-center gap-3 mb-6 sm:mb-8">
            <GraduationCap size={28} weight="duotone" className="text-[#E31E25] flex-shrink-0" />
            <span className="text-base sm:text-lg font-bold text-[#1A1A1A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Sasurie College of Arts and Science
            </span>
          </div>

          <p className="overline mb-3 sm:mb-4">Administrator Access</p>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tighter text-[#1A1A1A] mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Sign In
          </h2>
          <p className="text-sm sm:text-base text-[#666666] mb-6 sm:mb-8">
            Enter your credentials to access the admin dashboard
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-sm font-semibold text-[#1A1A1A]">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                {...register('email')}
                className="mt-2 rounded-none border-[#E8E8E8] focus:border-[#E31E25] focus:ring-[#E31E25]"
                placeholder="admin@sasurie.edu"
              />
              {errors.email && (
                <p className="text-[#E31E25] text-sm mt-1">{errors.email.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-semibold text-[#1A1A1A]">
                Password
              </Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  data-testid="login-password-input"
                  {...register('password')}
                  className="rounded-none border-[#E8E8E8] focus:border-[#E31E25] focus:ring-[#E31E25] pr-10"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666666] hover:text-[#1A1A1A]"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.password && (
                <p className="text-[#E31E25] text-sm mt-1">{errors.password.message}</p>
              )}
            </div>

            {error && (
              <div className="bg-[#E31E25]/10 border border-[#E31E25]/20 p-4 text-[#E31E25] text-sm">
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full bg-[#E31E25] hover:bg-[#C41A20] text-white py-6 rounded-none font-semibold text-base"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Signing in...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <SignIn size={20} weight="bold" />
                  Sign In
                </div>
              )}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-[#E8E8E8]">
            <p className="text-sm text-[#666666] text-center">
              Go back to{' '}
              <a href="/" className="text-[#E31E25] hover:text-[#C41A20] font-medium">
                Results Portal
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
